import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import {
  MessageCircle,
  Calendar,
  ShieldCheck,
  BarChart3,
  Users,
  Ear,
  Eye,
  Zap,
} from "lucide-react";
import metaPartner from "@/assets/partners/meta.svg";
import googlePartner from "@/assets/partners/google.svg";
import googleCloudPartner from "@/assets/partners/google-cloud.svg";
import shopifyPartner from "@/assets/partners/shopify.svg";
import tiktokPartner from "@/assets/partners/tiktok.svg";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import TeamSection from "@/components/sections/TeamSection";
import { Button } from "@/components/ui/button";
import { SITE } from "@/lib/seo";

const TITLE = "Quiénes somos: el equipo de Vibra Marketing | Vibra";
const DESCRIPTION =
  "Vibra Marketing es una agencia de marketing digital para PYMEs con sede en Madrid. Conoce al equipo, nuestra filosofía y por qué más de 70 empresas confían en nosotros.";
const URL = `${SITE.url}/sobre-nosotros`;

const breadcrumb = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Inicio", item: `${SITE.url}/` },
    { "@type": "ListItem", position: 2, name: "Sobre nosotros", item: URL },
  ],
};

const aboutPage = {
  "@context": "https://schema.org",
  "@type": "AboutPage",
  url: URL,
  name: TITLE,
  description: DESCRIPTION,
  about: { "@id": `${SITE.url}/#organization` },
};

const stats = [
  { value: "+70", label: "empresas confían en Vibra" },
  { value: "4,8", label: "valoración en Trustpilot" },
  { value: "+5M€", label: "presupuestados para clientes" },
  { value: "100%", label: "retención de clientes" },
];

const philosophy = [
  {
    Icon: Ear,
    title: "Primero escuchamos",
    desc: "Entendemos tu negocio, tus números y tus objetivos antes de proponerte nada.",
  },
  {
    Icon: Eye,
    title: "Transparencia radical",
    desc: "Precios públicos con IVA, alcances por escrito, sin permanencia. Tus cuentas son tuyas.",
  },
  {
    Icon: Zap,
    title: "Tecnología que multiplica",
    desc: "Automatizamos embudos, tareas e informes para que tu inversión vaya a estrategia, no a horas.",
  },
];

const howWeWork = [
  {
    Icon: ShieldCheck,
    title: "Sin letra pequeña",
    desc: "Precios públicos, alcances por escrito y sin permanencia.",
  },
  {
    Icon: BarChart3,
    title: "Todo medido",
    desc: "Reportes mensuales con los datos que importan — leads y ventas, no humo.",
  },
  {
    Icon: Users,
    title: "Tu equipo, no un proveedor",
    desc: "Una reunión estratégica por cada servicio contratado, cada mes.",
  },
];

const SobreNosotros = () => (
  <div className="min-h-screen bg-background">
    <Helmet>
      <title>{TITLE}</title>
      <meta name="description" content={DESCRIPTION} />
      <link rel="canonical" href={URL} />
      <meta property="og:type" content="website" />
      <meta property="og:title" content={TITLE} />
      <meta property="og:description" content={DESCRIPTION} />
      <meta property="og:url" content={URL} />
      <meta property="og:image" content={SITE.ogImage} />
      <meta name="twitter:card" content="summary_large_image" />
      <script type="application/ld+json">{JSON.stringify(aboutPage)}</script>
      <script type="application/ld+json">{JSON.stringify(breadcrumb)}</script>
    </Helmet>
    <Header />
    <main className="pt-24 pb-16">
      {/* Breadcrumb */}
      <div className="container px-6">
        <nav aria-label="Migas de pan" className="mb-6 text-sm text-muted-foreground">
          <ol className="flex flex-wrap items-center gap-2">
            <li><Link to="/" className="hover:text-foreground">Inicio</Link></li>
            <li aria-hidden>/</li>
            <li className="text-foreground font-medium">Sobre nosotros</li>
          </ol>
        </nav>
      </div>

      {/* Hero */}
      <header className="container px-6 pt-16 pb-52 text-center max-w-3xl mx-auto">
        <h1 className="font-display text-4xl md:text-6xl uppercase mb-6">
          La agencia que empieza por escucharte
        </h1>
        <p className="text-lg text-muted-foreground leading-relaxed">
          Agencia de marketing digital para PYMEs con sede en Madrid. Más de{" "}
          <strong className="text-foreground">70 empresas</strong> confían en
          nosotros, con un <strong className="text-foreground">4,8 en Trustpilot</strong>.
        </p>
        <div className="mt-8 pt-6 border-t border-border/50">
          <p className="text-xs uppercase tracking-widest text-muted-foreground text-center mb-5 font-body">Partners oficiales</p>
          <div className="flex flex-nowrap items-center justify-center gap-x-4 md:gap-x-6">
            <img src={googlePartner} alt="Google Partner" width={140} height={48} loading="lazy" decoding="async" className="h-8 md:h-10 w-auto opacity-70 hover:opacity-100 transition-opacity grayscale hover:grayscale-0 min-w-0" />
            <img src={metaPartner} alt="Meta Business Partner" width={140} height={48} loading="lazy" decoding="async" className="h-8 md:h-10 w-auto opacity-70 hover:opacity-100 transition-opacity grayscale hover:grayscale-0 min-w-0" />
            <img src={googleCloudPartner} alt="Google Cloud Partner" width={140} height={48} loading="lazy" decoding="async" className="h-8 md:h-10 w-auto opacity-70 hover:opacity-100 transition-opacity grayscale hover:grayscale-0 min-w-0" />
            <img src={shopifyPartner} alt="Shopify Partner" width={140} height={48} loading="lazy" decoding="async" className="h-8 md:h-10 w-auto opacity-70 hover:opacity-100 transition-opacity grayscale hover:grayscale-0 min-w-0" />
            <img src={tiktokPartner} alt="TikTok Marketing Partner" width={140} height={48} loading="lazy" decoding="async" className="h-8 md:h-10 w-auto opacity-70 hover:opacity-100 transition-opacity grayscale hover:grayscale-0 min-w-0" />
          </div>
        </div>
      </header>

      {/* Cifras */}
      <section className="container px-6 mb-40">
        <div className="max-w-5xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-4">
          {stats.map((s) => (
            <div
              key={s.label}
              className="p-6 rounded-2xl border border-primary/20 bg-gradient-to-br from-primary/5 to-transparent text-center shadow-[0_0_40px_-25px_hsl(var(--primary)/0.4)]"
            >
              <div className="font-display text-3xl md:text-4xl text-gradient font-black mb-2">
                {s.value}
              </div>
              <div className="text-xs md:text-sm text-muted-foreground leading-snug">
                {s.label}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Filosofía: 3 cards */}
      <section className="container px-6 mb-20">
        <div className="max-w-5xl mx-auto">
          <h2 className="font-display text-3xl md:text-5xl uppercase mb-3 text-center">
            Nuestra <span className="text-gradient">filosofía</span>
          </h2>
          <p className="text-center text-muted-foreground mb-10">
            Vibra nació para hacer exactamente lo contrario que las agencias de plantilla.
          </p>
          <div className="grid md:grid-cols-3 gap-5">
            {philosophy.map(({ Icon, title, desc }) => (
              <div
                key={title}
                className="p-7 rounded-2xl border border-border bg-card hover:border-primary/30 transition-colors"
              >
                <div className="w-12 h-12 rounded-xl bg-primary/10 text-primary flex items-center justify-center mb-5">
                  <Icon className="w-5 h-5" />
                </div>
                <h3 className="font-display text-xl uppercase mb-2">{title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Equipo */}
      <TeamSection />


      {/* Cómo trabajamos */}
      <section className="container px-6 py-20">
        <h2 className="font-display text-3xl md:text-4xl uppercase mb-10 text-center">
          Cómo trabajamos
        </h2>
        <div className="max-w-5xl mx-auto grid md:grid-cols-3 gap-6">
          {howWeWork.map(({ Icon, title, desc }) => (
            <div
              key={title}
              className="p-6 rounded-2xl border border-border bg-card"
            >
              <Icon className="text-primary mb-4" size={28} />
              <h3 className="font-semibold mb-2">{title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA final */}
      <section className="container px-6 pb-8">
        <div className="max-w-3xl mx-auto p-10 rounded-2xl bg-gradient-to-r from-primary/5 to-accent/5 border border-primary/20 text-center">
          <h2 className="font-display text-3xl md:text-4xl uppercase mb-3">
            ¿Quieres conocernos?
          </h2>
          <p className="text-muted-foreground mb-6">
            Agenda una llamada gratuita de 20 minutos.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <Link to="/contacto">
              <Button size="lg" className="gap-2">
                <Calendar size={18} /> Agendar reunión
              </Button>
            </Link>
            <a
              href={SITE.whatsappLink}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button size="lg" variant="outline" className="gap-2">
                <MessageCircle size={18} /> WhatsApp
              </Button>
            </a>
          </div>
        </div>
      </section>
    </main>
    <Footer />
  </div>
);

export default SobreNosotros;
